import { useCallback } from 'react';
import { useAuth } from '@clerk/react';
import { useQueryClient } from '@tanstack/react-query';
import { useEditorStore } from '../../../stores/editor.js';
import { quotaQueryKey } from '../../../hooks/use-quota-query.js';

const API_BASE = import.meta.env.VITE_API_BASE || '/api';

export interface SubmitResult {
  status: 'completed';
  imageUrl: string;
  tokenCost: number;
  remainingTokens: number;
  // Server flag: true when the route wrote a one-step revert snapshot for
  // this session. Mirrored into the editor store so the Revert button knows
  // whether to render.
  canRevert?: boolean;
}

export function useSubmitMask(sessionId: string | undefined) {
  const setSubmissionStatus = useEditorStore((s) => s.setSubmissionStatus);
  const setSubmissionError = useEditorStore((s) => s.setSubmissionError);
  const setResultImageUrl = useEditorStore((s) => s.setResultImageUrl);
  const setCurrentImageUrl = useEditorStore((s) => s.setCurrentImageUrl);
  const setCanRevert = useEditorStore((s) => s.setCanRevert);
  const submissionStatus = useEditorStore((s) => s.submissionStatus);
  const queryClient = useQueryClient();
  const { userId } = useAuth();

  const submit = useCallback(
    async (maskDataUrl: string, description: string): Promise<SubmitResult | null> => {
      if (!sessionId) return null;

      // P3: Guard against concurrent submissions
      if (useEditorStore.getState().submissionStatus === 'submitting') return null;

      const idempotencyKey = crypto.randomUUID();
      const maskBase64 = maskDataUrl.replace(/^data:image\/png;base64,/, '');

      // P5: Validate base64 payload after prefix stripping
      if (!maskBase64 || !/^[A-Za-z0-9+/]+=*$/.test(maskBase64)) {
        setSubmissionError('Invalid mask data. Please redraw your mask and try again.');
        setSubmissionStatus('error');
        return null;
      }

      setSubmissionStatus('submitting');
      setSubmissionError(null);

      try {
        const { quality } = useEditorStore.getState();
        const res = await fetch(`${API_BASE}/v1/edit/${sessionId}/submit`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Idempotency-Key': idempotencyKey,
          },
          body: JSON.stringify({
            mask_base64: maskBase64,
            instruction: description,
            quality_hint: quality,
          }),
        });

        if (res.ok) {
          const body = (await res.json()) as { data: SubmitResult };
          setSubmissionStatus('success');
          setResultImageUrl(body.data.imageUrl);
          // AC1: swap the canvas source to the newly generated image so the
          // user sees the result before deciding to Accept or Keep editing.
          setCurrentImageUrl(body.data.imageUrl);
          setCanRevert(body.data.canRevert ?? true);
          void queryClient.invalidateQueries({ queryKey: quotaQueryKey(userId) });
          return body.data;
        }

        const errBody = (await res.json()) as { error?: { code?: string; message?: string } };
        const message = errBody.error?.message ?? 'Generation failed. Please try again.';
        setSubmissionStatus('error');
        setSubmissionError(message);
        return null;
      } catch {
        setSubmissionStatus('error');
        setSubmissionError('Network error. Please try again.');
        return null;
      }
    },
    [
      sessionId,
      setSubmissionStatus,
      setSubmissionError,
      setResultImageUrl,
      setCurrentImageUrl,
      setCanRevert,
      queryClient,
      userId,
    ],
  );

  return { submit, isSubmitting: submissionStatus === 'submitting' };
}
